import * as React from "react";
import { useMemo } from "react";

import { Box, type BoxProps } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Icon } from "../../components/ui/icon";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "../../components/ui/menu";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Text } from "../../components/ui/text";
import type { GroupScope, SurfaceToken } from "../../components/ui/box-classes";
import { useElementSize } from "../../hooks/use-element-size";
import type { Color } from "../../lib/colors";
import { cn } from "../../lib/utils";
import { SCROLL_END_SLACK_CLASS, SCROLLPORT_CONTAINER_CLASS } from "../../lib/scroll-end-slack";
import { EditableTitleField } from "../inline-edit/inline-edit";
import {
  GRID_LAYER,
  WIDGET_CARD_ATTR,
  widgetCardDomId,
  type SortableGridDragHandleProps,
} from "../sortable-grid";
import {
  WidgetBodyLoadingProvider,
  WidgetSurfaceProvider,
} from "./contexts";
import {
  EMPTY_TITLE_CONTEXT,
  useWidgetTitleContext,
  type WidgetTitleContext,
} from "./control-context";
import { useWidgetRegistry } from "./registry-context";
import { useWidgetAutoHeight } from "./use-widget-auto-height";
import {
  WidgetEmptyActionHostProvider,
  WidgetEmptyActionPress,
  type WidgetEmptyActionSpec,
} from "./widget-empty-action";
import type { WidgetRegistry } from "./registry";
import type {
  WidgetConfig,
  WidgetInstance,
  WidgetPresentation,
  WidgetSurface,
} from "./types";
import { WidgetViewStateProvider } from "./view-state-context";
import { Widget } from "./widget";
import type { Omittable } from "../../lib/props";

/** The slack every widget scrollport ends on, in the spacing scale: a full
 *  `xl` (32px) under the last row, so the list travels clear of the edge
 *  fade instead of stopping with its last line pinned to the card's edge.
 *  The card's own scroller appends it as {@link WidgetScrollEndGutter}; a
 *  body that owns its scroller pads its content with the same token. */
export const SCROLL_END_GUTTER = "xl" as const;

/**
 * The end slack of a widget scrollport, appended after the body inside the
 * scroll content: the fixed gutter as its floor, and half the scrollport's
 * height on top (`SCROLL_END_SLACK_CLASS`), so the last row can travel up to
 * the middle of the card. It is scroll CONTENT, not padding on the wrapper:
 * the wrapper is a flex child sized to the viewport (`fill="size"`), so rows
 * that overflow it spill straight past its padding box and the padding never
 * reaches the end of the travel — the last row stopped pinned against the
 * card's edge with the inset drawn under it. As a sibling after the body it
 * lands where the rows end, and the scrollable overflow includes it.
 *
 * It exists only while the viewport overflows (`data-has-overflow-y`, set
 * by the scroll area on its viewport — the gutter's great-grandparent, hence
 * the scoped `>*>*>&` and not an `in-*` variant, which a scrolling PAGE
 * around the card would also satisfy). A body that fits keeps the card's
 * inset as its foot, so a chart or a short list is not pushed up by half a
 * card of nothing; a body that overflows by a pixel gains the whole slack
 * and stays overflowing, so the toggle cannot oscillate. The same gate is
 * what lets the card's scroller append it under EVERY body: a body that
 * owns its own bounded scrollport (a `bleed` table, a `bleed-bottom` list)
 * never overflows the card's viewport, so the gutter there is simply hidden.
 *
 * Seat it three levels under a `ScrollArea` viewport — the kit's frames do,
 * and a `flush` body that mounts its own `ScrollArea` seats it as the last
 * child of its content column. The ScrollArea's root must wear
 * `SCROLLPORT_CONTAINER_CLASS`, or the half-height has nothing to measure.
 */
export function WidgetScrollEndGutter() {
  return (
    <Box
      aria-hidden
      data-slot="widget-scroll-end-gutter"
      shrink={false}
      paddingBottom={SCROLL_END_GUTTER}
      className={cn(SCROLL_END_SLACK_CLASS, "hidden [[data-has-overflow-y]>*>*>&]:block")}
    />
  );
}

type WidgetBodyFrameProps = {
  /**
   * `auto-height`: the sizer the card measures; grows, never scrolls, the
   * grid grows the row span instead. `flush`: the widget owns its scrollport,
   * so no wrapper and no padding. `scroll`: the card scrolls the body behind
   * an edge fade and ends it on the scroll-end gutter.
   */
  mode: "auto-height" | "flush" | "scroll";
  contentRef: React.RefObject<HTMLDivElement | null>;
  paddings: Partial<Pick<BoxProps, "paddingX" | "paddingY" | "paddingTop" | "paddingBottom">>;
  children: React.ReactNode;
};

/**
 * The one body decision every card branch shares. Flex columns, never a
 * block: every widget body root claims its height with `fill` (flex-1
 * min-h-0), and in a block parent that is inert — the root collapses to
 * content height, and a self-measuring child (an XYChart's ParentSize, a map
 * canvas) resolves `height: 100%` against `auto` and renders at 0.
 */
function WidgetBodyFrame({ mode, contentRef, paddings, children }: WidgetBodyFrameProps) {
  if (mode === "auto-height") {
    return (
      <Box ref={contentRef} grow shrink={false} direction="column" {...paddings}>
        {children}
      </Box>
    );
  }
  if (mode === "flush") {
    return (
      <Box flex={1} fill="size" direction="column">
        {children}
      </Box>
    );
  }
  // scrollFade: the body dissolves at the card's edges only while content
  // continues past them, so a long list reads as a list that goes on rather
  // than one cut by the header line. The ScrollArea root is the size
  // container the gutter's half-height measures — its height is the body's,
  // definite under a grid cell or a dialog.
  return (
    <ScrollArea fill scrollFade className={SCROLLPORT_CONTAINER_CLASS}>
      <Box fill="size" direction="column" {...paddings}>
        {children}
        <WidgetScrollEndGutter />
      </Box>
    </ScrollArea>
  );
}

/** The display title for an instance. A module that declares `dynamicTitle`
 *  OWNS title resolution: it gets the resolved config — including any stored
 *  `config.title` — and its return value is the title (the widget's own
 *  mechanics decide whether a stored title is a user rename to honor or a
 *  spelling it generated itself to re-derive). When the module declares no
 *  resolver (or returns undefined): `config.title`, else the static default.
 *  `context` is what the resolver may read beyond the config (the mounted
 *  controls' option labels — `useWidgetTitleContext`); absent, no label
 *  resolves. */
export function widgetTitle(
  instance: WidgetInstance,
  registry: WidgetRegistry,
  context: WidgetTitleContext = EMPTY_TITLE_CONTEXT,
): string {
  const cfgTitle = instance.config?.title;
  const stored = typeof cfgTitle === "string" && cfgTitle.length > 0 ? cfgTitle : undefined;
  const def = registry.get(instance.type);
  if (!def) return stored ?? instance.type;
  const resolved = def.dynamicTitle?.(
    registry.resolveConfig(def, instance.config, instance.schemaVersion ?? 1),
    context,
  );
  return resolved ?? stored ?? def.title;
}

/** One discrete width choice the host offers in the card's ellipsis menu. */
export type WidgetWidthOption = { value: number; label: string };

export type WidgetCardProps = Omittable<{
  instance: WidgetInstance;
  canManage: boolean;
  /** Layout authoring (drag-reorder + edge resize) split from `canManage` —
   *  managed surfaces hide manage chrome but keep arranging; mobile hosts pass
   *  false for view-only. Defaults to `canManage`. */
  canArrange?: boolean;
  /** The viewer may only READ this widget. The module's `headerActions` —
   *  its transient controls (a filter, a mode switch) — are not rendered.
   *  Distinct from `canManage`, which gates the structural chrome (gear,
   *  rename, duplicate, delete): a reader holds neither, but a manager may
   *  still be told to keep their hands off the data. */
  consumeOnly?: boolean;
  /** Layout participation — the VIEW decides. `'grid'` (default): fixed cell,
   *  body owns scroll. `'stack'`: page flow — the card hugs content and the
   *  page scrolls (the issue view). */
  presentation?: WidgetPresentation;
  /** Which surface this card paints — the VIEW decides. See
   *  {@link WidgetSurface}. Resolution (the ONE place): `'canvas'` wins over
   *  everything (uniform substrate skin); else module `chromeless` forces
   *  `'canvas'`; else this value wins. `'none'` renders the body with NO
   *  shell at all. */
  surface?: WidgetSurface;
  /** The surface the BOARD behind the cards paints. `surface="canvas"`
   *  fills this surface exactly, so such a card masks the authoring guide
   *  dots by construction; `card` and `elevated` paint on top of it
   *  regardless. Defaults to `background` — a dashboard is a page. A
   *  dashboard mounted inside a card or a dialog names that surface
   *  instead. */
  canvas?: SurfaceToken;
  /** Stack-only disclosure: the header toggles the body. Grid ignores these. */
  collapsible?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Stack-only: fires when the card becomes, or stops being, an action row —
   *  a headed stack card whose body registered a `WidgetEmptyAction` and so
   *  shows "Add …" in the header's place. A host that lays sections out
   *  (sinks empty ones, keeps them out of a reorder) reads its stack's shape
   *  through this rather than knowing what each body holds. Called on mount
   *  with the initial answer and again on every change. */
  onActionRowChange?: (actionRow: boolean) => void;
  /** Stack-only: render the body without title/disclosure chrome. */
  hideHeader?: boolean;
  dragHandleProps?: SortableGridDragHandleProps;
  isDragging?: boolean;
  /** True while THIS card is being edge-resized. The card itself renders no
   *  resize chrome (the grid owns the live span badge) — forwarded for custom
   *  card slots that want their own hint. */
  isResizing?: boolean;
  /** Live (preview-aware) span from the grid. While resizing these track the
   *  previewed cell so the body re-lays-out in real time; at rest they fall back
   *  to the committed instance span. */
  liveW?: number;
  liveH?: number;
  /** Consumer-resolved title override. `undefined` → kit fallback
   *  ({@link widgetTitle}: config.title, else module title). `null` → NO
   *  fallback: an empty config title renders no title. */
  title?: string | null;
  /** Leading header slot — the consumer supplies the node (e.g. a palette
   *  Badge). The kit renders placement only; it resolves no visuals. */
  icon?: React.ReactNode;
  /** Selection state — paints the card border `brand` (never-neutral accent). */
  selected?: boolean;
  /** Fired when the title area or body is clicked (e.g. select into an
   *  inspector). On a headerless tile the whole tile click fires it. */
  onSelect?: () => void;
  /** Trailing header cluster before the actions (badges, lock icons). On a
   *  headerless tile it joins the floating hover cluster. */
  headerAccessories?: React.ReactNode;
  /** Semantic edge accent: paints a thin color bar down the card's left edge
   *  (e.g. a per-owner tint on a shared canvas). Colors come from the shared
   *  `Color` scale; `undefined` renders no bar. Ignored on `surface="none"`. */
  accent?: Color;
  /** Maximize affordance — renders the widget fullscreen at its largest layout.
   *  `undefined` hides the button and menu item. */
  onMaximize?: () => void;
  /** Gear/settings affordance — opens the widget's settings dialog.
   *  `undefined` hides the gear. */
  onConfigure?: () => void;
  /** `undefined` hides the Delete menu item. */
  onDelete?: () => void;
  /** `undefined` hides the Duplicate menu item. */
  onDuplicate?: () => void;
  /** Swap this widget for a different module in place (opens the consumer's
   *  picker). `undefined` hides the Replace menu item. */
  onReplace?: () => void;
  /** Drop the preset lock a curated instance carries (see
   *  `WidgetCatalogEntry.locks`) so every setting and the title open up. The
   *  menu item renders only while the instance is actually locked;
   *  `undefined` hides it outright. */
  onConvertToCustom?: () => void;
  /** Snap the tile back to its widget definition's default span.
   *  `undefined` hides the Reset-size menu item. */
  onResetSize?: () => void;
  /** Commit a new title to `config.title` (in-card editable title). */
  onRename?: (title: string) => void;
  /** Persist a config patch from in-widget interaction (e.g. a table column resize). */
  onConfigChange?: (patch: WidgetConfig) => void;
  /** Host-owned width presets shown in the ellipsis menu — instant sizes
   *  beside the side-edge drag, never a replacement for it. A preset outside
   *  the definition's span renders disabled with the reason. */
  widthOptions?: readonly WidgetWidthOption[];
  /** Commit a host-owned width choice. */
  onWidthChange?: (width: number) => void;
  /** Natural content height (px) of an `autoHeight` widget — the grid host
   *  converts to whole rows (input-only px→rows, same role as pointer
   *  geometry) and feeds its derived live layout. Wired by `WidgetGrid`.
   *  `snap` marks a load-time settle (a skeleton's height, or the first real
   *  content height after one): the host commits it without the FLIP spring. */
  onContentHeightChange?: (px: number, opts?: { snap?: boolean }) => void;
  /** Hover-group scope for the card's hover-revealed chrome. Nested children
   *  inside a container card use `'tile'` so only the innermost hovered card
   *  lights its cluster (different scopes nest cleanly). Defaults to `'box'`. */
  hoverScope?: HoverScope;
  /** Hide the hover-revealed action chrome while any descendant widget card is
   *  hovered (container cards: hovering a child shows only the child's
   *  cluster). Static class — display beats the opacity reveal. */
  suppressActionsOnDescendantHover?: boolean;
}>;

/** The two hover scopes a widget card can own — a subset of Box's `GroupScope`
 *  so the card's `group` / `hoverReveal` pair is always a legal Box scope. A
 *  nested child card takes `'tile'` so only the innermost hovered card lights
 *  its cluster. */
type HoverScope = Extract<GroupScope, "box" | "tile">;

/** Hides an element while any descendant `[data-widgetcard]` (a nested child
 *  card, stamped via `WIDGET_CARD_ATTR`) is hovered, scoped to the card's own
 *  hover group. Tailwind only emits classes present verbatim in source, so
 *  both spellings stay literal. */
const SUPPRESS_ON_DESCENDANT_HOVER: Record<HoverScope, string> = {
  box: "group-has-[[data-widgetcard]:hover]/box:hidden",
  tile: "group-has-[[data-widgetcard]:hover]/tile:hidden",
};

/** Arrange-gated hover cue, keyed by the surface the card paints so the cue is
 *  that surface's own louder line rather than the page's: `elevated` rests on
 *  a quieter hairline than `card`, and a cue has to clear the line it
 *  replaces. Literal strings for the Tailwind scanner. */
const HOVER_BORDER: Record<"card" | "elevated", string> = {
  card: "hover:border-(--card-border-strong) transition-colors",
  elevated: "hover:border-(--elevated-border-strong) transition-colors",
};

/** A card pinned to the canvas keeps the card's SHAPE and drops every bit of
 *  its chrome: no drop, no inner highlight, and a hairline that is reserved
 *  rather than absent so the geometry never shifts when a cue colors it in.
 *  The fill still comes from `Box surface` — only the chrome is dropped. */
const CANVAS_CHROME = "border-transparent shadow-none before:hidden";

/** The staged header fold, keyed on RENDERED width (a span is a fraction of a
 *  grid whose pixel size the card cannot know, so the same span is roomy on a
 *  wide monitor and cramped in a split window). Two tiers:
 *  - below `SETTINGS_FOLD_WIDTH`, Settings — the least-used header control —
 *    trades its button for a menu item, giving the hovered title its room;
 *  - below `SMALL_CARD_WIDTH`, Maximize follows it into the menu. Budgeted at
 *    the compact 12px inset: two insets + three 24px `icon-xs` buttons with
 *    4px gaps (~80px cluster) + a ~28px domain control leaves about 140px of
 *    title — any thinner and even a short title collides with chrome.
 *
 *  Widget-contributed header actions never fold. A filter is the control a
 *  reader reaches for most, and a control behind the "…" is a control nobody
 *  finds; it holds its place beside the menu at every width, which the fold
 *  affords because the two manage buttons have already left by then. */
export const SETTINGS_FOLD_WIDTH = 360;
const SMALL_CARD_WIDTH = 280;

/**
 * THE chrome around a widget body — the only widget chrome in the system; custom
 * `renderCard` slots compose this component with the slot props (`icon`,
 * `title`, `selected`, `onSelect`, `headerAccessories`), never a
 * parallel Card tree.
 *
 * ONE owner per appearance axis:
 * - module `chrome` ('card' | 'headerless' | 'container') — STRUCTURE (headed
 *   / headerless / container-pill) plus the `body` axis and the body flags
 *   (`bodyDraggable`, `autoHeight`) — what the widget IS, never per view.
 * - module `chromeless` — "renders as content, not a card".
 * - host `presentation` ('grid' | 'stack') — layout participation.
 * - host `surface` ('card' | 'elevated' | 'canvas' | 'none') — what it paints.
 *
 * Resolution (here, nowhere else): `canvas` wins over everything and pins
 * every card to the substrate; else module `chromeless` forces `canvas`; else
 * the host's value wins. `accent` stays orthogonal on any surface but `none`.
 *
 * Elevation is named surfaces, never ad-hoc shadows, and the prop value IS
 * the surface name — there is no mapping to learn. `elevated` is a card
 * floating over the page: a longer, more diffuse drop and a quieter hairline
 * than `card`. An arrangeable card strengthens its hairline on hover. A
 * dragging card borrows the `popover` shadow on top of either, the one
 * moment it is truly above the page. `canvas` paints the board's own
 * surface — the `canvas` prop, which the host names (default `background`,
 * the page). Whatever the card resolves to paint it publishes through
 * `WidgetSurfaceProvider`, so a body's own opaque parts (a DataTable's sticky
 * header) blend with the card via `useWidgetSurface()`.
 */
export function WidgetCard({
  instance,
  canManage,
  canArrange,
  consumeOnly = false,
  presentation = "grid",
  surface = "card",
  canvas = "background",
  collapsible = false,
  collapsed = false,
  onCollapsedChange,
  onActionRowChange,
  hideHeader = false,
  dragHandleProps,
  isDragging,
  liveW,
  liveH,
  title,
  icon,
  selected,
  onSelect,
  headerAccessories,
  accent,
  onMaximize,
  onConfigure,
  onDelete,
  onDuplicate,
  onReplace,
  onConvertToCustom,
  onResetSize,
  onRename,
  onConfigChange,
  widthOptions,
  onWidthChange,
  onContentHeightChange,
  hoverScope = "box",
  suppressActionsOnDescendantHover,
}: WidgetCardProps) {
  const registry = useWidgetRegistry();
  const def = registry.get(instance.type);
  const arrange = canArrange ?? canManage;
  const chrome = def?.chrome ?? "card";
  const stack = presentation === "stack";
  const headerMode = chrome === "card" || stack ? "default" : "none";
  const containerChrome = !stack && chrome === "container";
  const bodyMode = def?.body ?? "inset";
  /** Horizontal bleed: the body reaches the card's side edges. */
  const bleed = bodyMode === "bleed";
  /** End bleed: the body owns the scrollport's end. */
  const bleedEnd = bodyMode === "bleed" || bodyMode === "bleed-bottom";
  const leadingRail = def?.leadingRail ?? false;
  const flushBody = bodyMode === "flush";
  const bodyDraggable = def?.bodyDraggable ?? false;
  const autoHeight = (def?.autoHeight ?? false) && !stack;
  const HeaderActions = consumeOnly ? undefined : def?.headerActions;
  const HeaderCaption = def?.headerCaption;
  const ModuleMenuItems = def?.menuItems;
  const TitleAdornment = def?.titleAdornment;

  // The body's one empty action (see `WidgetEmptyAction`). Only a stack card
  // with a header can lift it into the header; everywhere else the body
  // renders the row itself and this stays null.
  const hostsEmptyAction = stack && !hideHeader;
  const [emptyAction, setEmptyAction] = React.useState<WidgetEmptyActionSpec | null>(null);
  const actionRow = hostsEmptyAction ? emptyAction : null;
  const moduleIcon = def?.icon;
  const emptyActionHost = useMemo(
    () => ({ hosted: hostsEmptyAction, icon: moduleIcon, set: setEmptyAction }),
    [hostsEmptyAction, moduleIcon],
  );
  // A section that just gained its first item opens: a collapsed flag saved
  // while the card was an action row says nothing about content the reader
  // has never seen, and hiding what they just added is the one wrong answer.
  const wasActionRow = React.useRef(false);
  React.useEffect(() => {
    if (wasActionRow.current && actionRow == null && collapsed) onCollapsedChange?.(false);
    wasActionRow.current = actionRow != null;
  }, [actionRow, collapsed, onCollapsedChange]);
  const isActionRow = actionRow != null;
  React.useEffect(() => {
    onActionRowChange?.(isActionRow);
  }, [isActionRow, onActionRowChange]);
  // Header-slot renderers get the same resolved config as the body (the
  // harness resolves again for the body — same memoized inputs, cheap).
  const resolvedConfig = useMemo(
    () => registry.resolveConfig(def, instance.config, instance.schemaVersion ?? 1),
    [registry, def, instance.config, instance.schemaVersion],
  );

  // ONE resolution point for what the card paints (see the component doc).
  // A chromeless module renders as content — no fill at all — which removes
  // even more than `canvas`, so a host that already pinned every card to the
  // canvas keeps that and the module flag does not apply.
  // `none` never reaches a shell — it returns the bare body below — so the
  // paintable set excludes it and every branch can name a real surface.
  const chromeless = surface !== "canvas" && (def?.chromeless ?? false);
  const paints: Exclude<WidgetSurface, "none"> =
    chromeless || surface === "none" ? "canvas" : surface;
  // A painted card names a real surface and wears the full card chrome. A
  // canvas card wears the shape only. Every appearance branch below asks this
  // once. `bodySurface` is what the body actually sits on — the card's own
  // fill, or the board's for a canvas/none card — published to the body.
  const painted = paints !== "canvas";
  const bodySurface: SurfaceToken = painted ? paints : canvas;

  const titleContext = useWidgetTitleContext();
  const resolvedTitle =
    title === undefined ? widgetTitle(instance, registry, titleContext) : (title ?? "");
  const w = liveW ?? instance.w;
  const h = liveH ?? instance.h;
  // A select click is consumed here: nested child cards render INSIDE a
  // container tile's own select surface, so a bubbling click would re-select
  // the ancestor and clobber the child selection — innermost card wins.
  const handleSelect = onSelect
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect();
      }
    : undefined;
  // A tiny card (≤2 cells) hides Maximize — the only thing the chrome needs to
  // know about size.
  const compact = w * h <= 2;
  // The grid card's rendered width, measured on the Card node itself so the
  // observer follows it when the presentation branches swap it out. While the
  // shell pane animates its width every card resizes per frame; the commits
  // are held through the layout-settle window (`useElementSize`) and the
  // card's content slides under the clip meanwhile.
  const { ref: cardSizeRef, size: cardSize } = useElementSize<HTMLDivElement>();
  const cardWidth = cardSize.width > 0 ? cardSize.width : null;
  // The staged fold (see the fold constants). Until the first measurement
  // lands (and on hosts that never mount the measured Card) the span stands
  // in for both tiers.
  const small =
    !stack && (cardWidth != null ? cardWidth < SMALL_CARD_WIDTH : w <= 3);
  const foldSettings =
    small || (!stack && cardWidth != null && cardWidth < SETTINGS_FOLD_WIDTH);
  // Match the COSS Card density axis to the live widget size. Small widgets
  // use the 12px compact inset; larger widgets use the 16px regular inset.
  // Both terms are live — the span during a resize preview, the rendered
  // width through the observer — so the body and header tighten or relax
  // together instead of waiting for the persisted span.
  const cardDensity = compact || small ? "compact" : "regular";
  const cardInset = compact || small ? ("base" as const) : ("md" as const);

  // The drag surface (header row, whole tile, or — for bodyDraggable — the
  // whole card). We wrap `onKeyDown` so a keyboard drag only starts when the
  // handle itself is the focused target — otherwise Space/Enter typed into the
  // title editor would bubble up and grab the card.
  const guardedDragProps: SortableGridDragHandleProps | undefined =
    arrange && dragHandleProps
      ? {
          ...dragHandleProps,
          onKeyDown: (event) => {
            if (event.target === event.currentTarget) {
              dragHandleProps.onKeyDown?.(event);
            }
          },
        }
      : undefined;
  const dragCursor = guardedDragProps ? (isDragging ? "grabbing" : "grab") : undefined;
  // Stack cards drag only from the grip seated outside the header capsule;
  // bodyDraggable grid cards drag from the whole card; everything else drags
  // from its header/tile.
  const cardDragProps = !stack && bodyDraggable ? guardedDragProps : undefined;
  const headerDragProps = stack || bodyDraggable ? undefined : guardedDragProps;
  const stackGutterDragProps = stack ? guardedDragProps : undefined;

  const { ref: headerActivatorRef, ...headerDragRest } = headerDragProps ?? {};
  const { contentRef, setHeaderNode, bodyLoadingRegistry, measure } = useWidgetAutoHeight({
    enabled: autoHeight,
    cardRef: cardSizeRef,
    headerActivatorRef,
    onContentHeightChange,
  });

  const stopHeaderPropagation = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
  };

  // Hover-revealed chrome for the FLOATING clusters (headerless tile,
  // container pill): opacity-based, spread from Box so the group pair can
  // never drift; on a coarse pointer the tap that arms the widget reveals it.
  // Header chrome reveals by DISPLAY instead (`revealMode="display"`), so it
  // occupies no layout at rest and the resting title runs the full header.
  const reveal = { hoverReveal: hoverScope } as const;
  // Affordance gates. Maximize is NOT manage-gated (viewers open the detail
  // dialog's data view); Settings and the manage menu items are; width options
  // are host-gated (the host only passes them where width changes are allowed).
  const showMaximize = !!onMaximize && (def?.chromeActions?.maximize ?? true);
  const showConfigure = canManage && !!onConfigure && (def?.chromeActions?.settings ?? true);
  const widthMenu = (widthOptions?.length ?? 0) > 0;
  // Preset locks: a curated instance keeps its locked settings fixed until the
  // host converts it to custom — the menu item shows only while it is bound.
  const convertToCustom =
    registry.lockedKeys(instance.type, resolvedConfig).size > 0 ? onConvertToCustom : undefined;
  const manageMenuItems =
    canManage && !!(onDuplicate || onReplace || convertToCustom || onResetSize || onDelete);
  // Maximize/Settings appear as MENU items only when their header button is
  // collapsed away — every separator below must gate on these collapsed terms
  // (not the raw affordances) or it renders orphaned above/below nothing.
  const menuMaximize = showMaximize && (small || compact);
  const menuConfigure = showConfigure && foldSettings;
  const moduleMenu = ModuleMenuItems != null;
  const menuAvailable =
    widthMenu || manageMenuItems || menuMaximize || menuConfigure || moduleMenu;
  const clusterAvailable = showMaximize || showConfigure || menuAvailable;
  // Header action ORDER: Maximize → domain headerActions → Settings → menu;
  // Maximize/Settings collapse into the menu at small widths.
  const actionCluster = clusterAvailable ? (
    <>
      {showMaximize && !compact && !small ? (
        <Button variant="ghost" size="icon-xs" onClick={onMaximize} aria-label="Maximize">
          <Icon name="maximize-2" size="sm" color="muted" />
        </Button>
      ) : null}
      {showConfigure && !foldSettings ? (
        <Button variant="ghost" size="icon-xs" onClick={onConfigure} aria-label="Settings">
          <Icon name="settings" size="sm" color="muted" />
        </Button>
      ) : null}
      {menuAvailable ? (
        <Menu>
          <MenuTrigger
            render={
              <Button variant="ghost" size="icon-xs" aria-label="Widget menu">
                <Icon name="ellipsis" size="sm" color="muted" />
              </Button>
            }
          />
          <MenuPopup align="end" sideOffset={4}>
            {ModuleMenuItems ? (
              <ModuleMenuItems
                w={w}
                h={h}
                collapsed={collapsed}
                config={resolvedConfig}
                onConfigChange={onConfigChange}
              />
            ) : null}
            {moduleMenu && (widthMenu || menuMaximize || menuConfigure || manageMenuItems) ? (
              <MenuSeparator />
            ) : null}
            {widthOptions?.map((option) => {
              // A preset outside the definition's span stays listed so the
              // vocabulary is stable across cards, but it cannot commit: the
              // grid would clamp it, and a row that silently does something
              // else is worse than one that says why it is off.
              const reason =
                def && option.value < def.span.minW
                  ? "This widget can't be narrower"
                  : def && option.value > def.span.maxW
                    ? "This widget can't be wider"
                    : undefined;
              return (
                <MenuItem
                  key={option.value}
                  disabled={!!reason}
                  disabledReason={reason}
                  onClick={() => onWidthChange?.(option.value)}
                >
                  <Icon name={w === option.value ? "check" : "columns"} size="sm" />
                  {option.label}
                </MenuItem>
              );
            })}
            {widthMenu && (manageMenuItems || menuMaximize || menuConfigure) ? (
              <MenuSeparator />
            ) : null}
            {menuMaximize ? (
              <MenuItem onClick={onMaximize}>
                <Icon name="maximize-2" size="sm" />
                Maximize
              </MenuItem>
            ) : null}
            {menuConfigure ? (
              <MenuItem onClick={onConfigure}>
                <Icon name="settings" size="sm" />
                Settings
              </MenuItem>
            ) : null}
            {canManage && onDuplicate ? (
              <MenuItem onClick={onDuplicate}>
                <Icon name="copy" size="sm" />
                Duplicate
              </MenuItem>
            ) : null}
            {canManage && onReplace ? (
              <MenuItem onClick={onReplace}>
                <Icon name="replace" size="sm" />
                Replace
              </MenuItem>
            ) : null}
            {canManage && convertToCustom ? (
              <MenuItem onClick={convertToCustom}>
                <Icon name="sliders-horizontal" size="sm" />
                Convert to custom
              </MenuItem>
            ) : null}
            {canManage && onResetSize ? (
              <MenuItem onClick={onResetSize}>
                <Icon name="minimize" size="sm" />
                Reset size
              </MenuItem>
            ) : null}
            {canManage &&
            onDelete &&
            (widthMenu ||
              menuMaximize ||
              menuConfigure ||
              onDuplicate ||
              onReplace ||
              convertToCustom ||
              onResetSize) ? (
              <MenuSeparator />
            ) : null}
            {canManage && onDelete ? (
              <MenuItem onClick={onDelete}>
                <Icon name="trash" size="sm" />
                Delete
              </MenuItem>
            ) : null}
          </MenuPopup>
        </Menu>
      ) : null}
    </>
  ) : null;

  // Widget-contributed header controls: summoned with the manage cluster but
  // NOT manage-gated (viewers can filter). They sit in the header at every
  // width — a filter is the most-reached-for control on a card, and one
  // rendered inside the "…" menu is one a reader never finds.
  const domainActions =
    HeaderActions ? (
      <Box direction="row" align="center">
        <HeaderActions
          w={w}
          h={h}
          collapsed={collapsed}
          config={resolvedConfig}
          onConfigChange={onConfigChange}
        />
      </Box>
    ) : null;

  // The module's standing caption: a fact the section states even folded. It
  // rides the accessories position — before the summoned chrome — so the
  // hover strip appears beside it, never over it.
  const headerCaption = HeaderCaption ? (
    <Box shrink={false} direction="row" align="center" onClick={stopHeaderPropagation}>
      <HeaderCaption
        w={w}
        h={h}
        collapsed={collapsed}
        config={resolvedConfig}
        onConfigChange={onConfigChange}
      />
    </Box>
  ) : null;

  // ONE summoned strip for the domain controls and the manage cluster. Split
  // into two summoned boxes, an open filter popover held only its own box
  // after the pointer left: the cluster beside it vanished and the chip slid
  // into the vacated width, under the popup it had just anchored. A single
  // `has-[[data-popup-open]]` / `focus-within` scope holds both in place.
  //
  // The header's own 4px gap is the rail-to-title rhythm; a summoned button
  // landing that close to a standing caption reads as the caption's label, so
  // on a card the strip carries a leading inset that seats the pair 12px
  // apart. It rides the strip, not the caption: the strip is
  // display-summoned, so at rest the caption still ends flush on the header's
  // trailing edge. A stack header parts them with its flexible spacer instead.
  //
  const seatBeforeStrip = !stack && (headerAccessories != null || headerCaption != null);
  const summonedChrome =
    domainActions || actionCluster ? (
      <Box
        direction="row"
        align="center"
        gap="xs"
        marginLeft={seatBeforeStrip ? "xs" : undefined}
        // Display-based, so the cluster occupies no header room until the
        // card is hovered, armed, focused or holding an open popup. A stack
        // card also shows it on a coarse pointer: its chrome is sparse, and
        // a held press is not a discoverable door.
        hoverReveal={hoverScope}
        revealMode="display"
        className={stack ? "pointer-coarse:flex" : undefined}
        onClick={stopHeaderPropagation}
        onKeyDown={stopHeaderPropagation}
      >
        {domainActions}
        {actionCluster ? (
          <Box direction="row" align="center" gap="2xs">
            {actionCluster}
          </Box>
        ) : null}
      </Box>
    ) : null;

  const body = (
    <WidgetEmptyActionHostProvider value={emptyActionHost}>
      {/* The ledger only matters where heights are measured; a fixed-height
          card passes `null` so its skeletons no-op. */}
      <WidgetBodyLoadingProvider value={measure ? bodyLoadingRegistry : null}>
        <Widget
          module={instance.type}
          config={instance.config ?? undefined}
          schemaVersion={instance.schemaVersion}
          w={w}
          h={h}
          onConfigChange={onConfigChange}
        />
      </WidgetBodyLoadingProvider>
    </WidgetEmptyActionHostProvider>
  );

  // `none`: NO shell — no Card, no header, no padding/scroll wrapper. The body
  // owns its entire presentation (print/read-only hosts). The view
  // state provider adds no DOM, so this stays pixel-identical to a raw body.
  if (surface === "none") {
    return (
      <WidgetViewStateProvider>
        <WidgetSurfaceProvider value={bodySurface}>{body}</WidgetSurfaceProvider>
      </WidgetViewStateProvider>
    );
  }

  // A manageable widget always shows the header (it's the drag handle and
  // title editor); an unmanaged one collapses it when there's nothing to show.
  const showHeader =
    headerMode === "default" &&
    !(stack && hideHeader) &&
    (canManage ||
      clusterAvailable ||
      resolvedTitle.length > 0 ||
      headerAccessories != null ||
      HeaderCaption != null ||
      HeaderActions != null);

  const suppressClass = suppressActionsOnDescendantHover
    ? SUPPRESS_ON_DESCENDANT_HOVER[hoverScope]
    : undefined;

  const stackCollapseLabel = `${collapsed ? "Expand" : "Collapse"} ${resolvedTitle}`;
  // The stack header's pressable is a CAPSULE, not the row, in two roles: the
  // disclosure that toggles the body, and the body's own empty action standing
  // in for the section (see `WidgetEmptyAction`). Same wash, same bleed, same
  // rung — the capsule changes role in place; it never changes shape.
  const stackToggle = stack && collapsible && actionRow == null;
  const actionGlyph = actionRow?.icon ?? moduleIcon;

  // The collapsible stack header's toggle is a CAPSULE around the title —
  // a LEADING solid caret (`▸` closed, `▾` open: the list group headers'
  // idiom), the section's own icon and its name — not the whole row. The
  // caret leads because it is the section's glyph: it stands on the same
  // 16px column as the rows' glyphs in the body below and the action rows'
  // glyphs beside it (the empty-action role seats the module glyph at the
  // capsule's content edge), so a stack of sections, property rows and
  // action rows reads as one glyph column with one label column. The wash
  // hugs the words the way the sidebar's does on its titles, and everything
  // that is not the toggle (the count adornment, the summoned action
  // cluster) lives OUTSIDE the capsule to its right, so a press on an action
  // can never read as a press on the section. The capsule's inset bleeds
  // into the gutter (negative start margin) so the caret itself sits on the
  // body content's edge — the header aligning with the page is the
  // invariant; the wash is hover-only chrome and may borrow gutter the
  // content never uses. The capsule is 32px — the property rows' control
  // rung, so a collapsed section and a property row are the same line in
  // the same list. The action role below wears the same 32px / `sm` / `md`
  // geometry.
  const titleBlock = (
    <Box
      minWidth={0}
      shrink
      direction="row"
      align="center"
      gap={stackToggle ? "xs" : "2xs"}
      height={stackToggle ? 32 : undefined}
      paddingX={stackToggle ? "sm" : undefined}
      marginLeftNegative={stackToggle ? "sm" : undefined}
      hoverBackground={stackToggle}
      rounded={stackToggle ? "md" : undefined}
      cursor={stackToggle ? "pointer" : undefined}
      onClick={stackToggle ? () => onCollapsedChange?.(!collapsed) : handleSelect}
      role={stackToggle ? "button" : undefined}
      tabIndex={stackToggle ? 0 : undefined}
      onKeyDown={stackToggle ? (event) => {
        if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onCollapsedChange?.(!collapsed);
        }
      } : undefined}
      aria-label={stackToggle ? stackCollapseLabel : undefined}
      aria-expanded={stackToggle ? !collapsed : undefined}
    >
      {stackToggle ? (
        // Always visible, the one resting hint that the capsule toggles —
        // pointing where the toggle will take the body. The registry's stable
        // caret keys, matching the list group header.
        <Icon name={collapsed ? "caret-right" : "caret-down"} size="sm" color="muted" />
      ) : null}
      {icon}
      {/* The title HUGS its text so the adornment ("· 12") sits BESIDE it —
          the flexible spacer after the adornment absorbs the leftover width
          and doubles as the header's click-to-select surface. A title with a
          glyph before it (icon or caret) keeps its inset; a bare one bleeds
          the inset to sit flush on the content edge. */}
      <EditableTitleField
        flushLeft={!icon && !stackToggle}
        // A title nobody can rename reserves no hover border: with the caret
        // before it, glyph + gap + the field's own inset is the property
        // rows' 24px glyph-to-label, and a reserved 1px would push it off.
        outline={Boolean(canManage && onRename)}
        // 14px in every presentation: a stack section's title reads on the
        // rows' own rung, one step up in weight, not in size.
        size="sm"
        weight="semibold"
        value={resolvedTitle}
        editable={Boolean(canManage && onRename)}
        onCommit={(next) => onRename?.(next)}
        // Named for its widget: every card on a board has a title field.
        aria-label={canManage && onRename ? `Rename ${resolvedTitle}` : undefined}
      />
    </Box>
  );

  const header = showHeader ? (
    <Box
      ref={setHeaderNode}
      direction="row"
      align="center"
      gap={stack ? "2xs" : "xs"}
      // A page-flow stack header carries no inset of its own: the title
      // capsule above bleeds its wash into the gutter and keeps the words on
      // the content edge. A painted card keeps the card inset.
      paddingX={stack && !painted ? undefined : cardInset}
      // A stack header is its 32px capsule and nothing more: the stack's own
      // gap is the space between sections, so a collapsed section keeps the
      // property rows' pitch. A card header keeps its insets.
      paddingTop={stack ? undefined : "sm"}
      // Tall enough for the SUMMONED action cluster (28px `icon-sm` domain
      // controls + this row's insets), not just the resting title: the summon
      // is display-based, so chrome taller than the resting line would GROW
      // the header on hover — the whole card jiggles in the stack, and on an
      // autoHeight grid tile the measured report crosses a row boundary. The
      // header owns its summoned height at rest; hover must move nothing.
      minHeight={stack ? 32 : 36}
      position={stackGutterDragProps ? "relative" : undefined}
      cursor={
        headerDragProps
          ? isDragging
            ? "grabbing"
            : "grab"
          : undefined
      }
      {...headerDragRest}
    >
      {stackGutterDragProps ? (
        // The stack card's only drag surface: a grip seated OUTSIDE the title
        // capsule, in the page gutter to its left. It rides the card's hover
        // group (pinned visible on touch, where hover never fires), and a
        // plain click — a drag that never moved — must not fall through to
        // the capsule's collapse toggle. The box spans the whole 24px gutter
        // as the hit target, but the glyph sits at the gutter's OUTER edge
        // (`justify="start"`), not its center: centered, the glyph ended 4px
        // from the title and overlapped the capsule's 8px wash bleed, reading
        // as part of the toggle. At the outer edge it clears the title by the
        // capsule's own 8px inset, and the grip's ink — narrower than its
        // 16px box — stays clear of the wash.
        <Box
          position="absolute"
          top={0}
          bottom={0}
          left={-24}
          width={24}
          direction="row"
          align="center"
          justify="start"
          cursor={isDragging ? "grabbing" : "grab"}
          hoverReveal={hoverScope}
          onClick={(event) => event.stopPropagation()}
          {...stackGutterDragProps}
        >
          <Icon name="grip-vertical" size="sm" color="muted" />
        </Box>
      ) : null}
      {actionRow ? (
        // The action role: a real button wearing the title capsule's exact
        // geometry — the module's glyph where the section icon sits and a
        // verb label where the title would — so a stack of sections and
        // action rows reads as one column of capsules. No title, no
        // adornment, no summoned chrome, no caret: the row IS the section's
        // one affordance, and a header control beside it would say the same
        // thing twice.
        <WidgetEmptyActionPress
          spec={actionRow}
          row={
            <Box
              as="button"
              type="button"
              direction="row"
              align="center"
              // Glyph to label: the property rows' 8px, so the verb lands on
              // the same column as their labels and the section titles.
              gap="sm"
              minWidth={0}
              height={32}
              paddingX="sm"
              marginLeftNegative="sm"
              hoverBackground
              rounded="md"
              cursor="pointer"
            >
              {actionGlyph ? <Icon name={actionGlyph} size="sm" color="muted" /> : null}
              {/* The title capsule's 14px, so a stack of sections and action
                  rows reads as one rung. */}
              <Text size="sm" truncate={1}>
                {actionRow.label}
              </Text>
            </Box>
          }
        />
      ) : (
        <>
          {titleBlock}
          {TitleAdornment ? (
            // Flex, not block: a bare block Box lays an inline adornment (a
            // count Text) on its own 16px-font strut baseline, seating the
            // smaller glyphs visibly below the title. Centering as a flex item
            // keeps the adornment on the header row's optical center instead.
            // In a stack section the count sits between the title capsule and
            // the icon-xs action buttons; those buttons inset their glyphs
            // ~4px inside a 24px box, and a bare count with no box of its own
            // read jammed against the first glyph. The `xs` inset gives the
            // count the same optical footprint, so count-to-glyph matches
            // glyph-to-glyph.
            <Box
              shrink={false}
              direction="row"
              align="center"
              paddingX={stack ? "xs" : undefined}
            >
              <TitleAdornment w={w} h={h} config={resolvedConfig} onConfigChange={onConfigChange} />
            </Box>
          ) : null}
          {/* Where the chrome sits depends on the presentation. A stack
              section seats its caption and accessories DIRECTLY after the
              title capsule — a fact about the section ("in 19 days", a
              count) reads with its name — and its summoned actions on the
              row's trailing edge, where every row's actions sit (the
              property rows' value controls, a table row's kebab): the
              header is [glyph, name, fact] … [actions], one column of
              actions down the stack instead of a verb chip trailing each
              title at a different x. A grid card keeps caption and cluster
              together on the card's trailing edge. */}
          {stack ? (
            <>
              {headerCaption}
              {headerAccessories}
              <Box flex={1} alignSelf="stretch" />
              {summonedChrome}
            </>
          ) : (
            <>
              <Box flex={1} alignSelf="stretch" onClick={handleSelect} />
              {headerCaption}
              {headerAccessories}
              {summonedChrome}
            </>
          )}
        </>
      )}
    </Box>
  ) : null;

  const accentBar = accent ? (
    <Box pointerEvents="none" position="absolute"
      data-color={accent}
      className="inset-y-0 left-0 w-1 bg-c"
    />
  ) : null;

  // ------ STACK PRESENTATION: a page-flow card that hugs content ------
  if (stack) {
    return (
      <WidgetViewStateProvider>
        <WidgetSurfaceProvider value={bodySurface}>
          <Card
            radius="lg"
            density={cardDensity}
            selected={selected}
            group={hoverScope}
            // Same resolution as the grid card below: the named surface comes
            // from Box, and a canvas card is a plain section — shape only, and
            // no fill at all, because a page-flow stack has no guide dots to
            // mask.
            surface={painted ? paints : undefined}
            surfaceShadow={painted && isDragging ? "popover" : undefined}
            className={cn(
              "p-0 overflow-visible transition-opacity",
              !painted && cn(CANVAS_CHROME, "bg-transparent"),
              selected && "border-brand",
              isDragging && "opacity-70",
            )}
          >
            {accentBar}
            {header}
            {/* ONE wrapper in every role, so the body never changes parent
              * — a body that swaps parents remounts and loses its state at
              * the very commit that changed the header's role. While the
              * header is an action row the wrapper takes no inset: the body
              * still mounts (it holds the registration and whatever picker
              * the action opens) but nothing sits under the row. A section
              * insets: rows keep a 24px inset and start 8px below the
              * header. Below, an OPEN section ends on `md`
              * (16px) before the stack's 4px gap: a collapsed section or an
              * action row is one line in the list and keeps the rows' pitch,
              * but a body is a block, and the next title needs a break from
              * its last row or the two sections read as one run (Spencer,
              * 2026-09-18 — 4px was too tight). A `leadingRail` module
              * supplies its own inset and does not receive another one from
              * the card. A collapsed body stays MOUNTED, hidden: only a
              * rendered body can declare its empty action, and a section
              * collapsed on a previous visit that has since emptied must
              * still come back as the action row, not as a folded title
              * over nothing. */}
            <Box
              hidden={collapsible && collapsed && actionRow == null}
              onClick={!isActionRow ? handleSelect : undefined}
              paddingX={!isActionRow && painted ? cardInset : undefined}
              paddingLeft={
                !isActionRow && !painted && showHeader && !leadingRail ? "lg" : undefined
              }
              paddingBottom={
                !isActionRow ? (painted ? cardInset : showHeader ? "md" : undefined) : undefined
              }
              paddingTop={!isActionRow && showHeader ? "sm" : undefined}
            >
              {body}
            </Box>
          </Card>
        </WidgetSurfaceProvider>
      </WidgetViewStateProvider>
    );
  }

  // ------ GRID PRESENTATION ------
  // Under a header the body starts `base` (12px) below it: the header's own
  // 8px top inset plus its 36px line already seat the title, and the first
  // row needs air of its own under it or the two read as one block (4px
  // was too tight — Spencer, 2026-09-15). An autoHeight body never scrolls —
  // the grid grows the row span instead — so the inset below its last row is
  // static whitespace with nothing to scroll away from, and it takes the
  // same `base` to match the inset above. That symmetry is what the sizer's
  // fill is for: the body reaches both insets, so the gutter a reader sees
  // around it is the inset itself rather than the inset plus however much
  // the row quantization left over. A scrolling body keeps the card inset
  // below as the foot of content that FITS; once the content overflows, the
  // travel ends on `WidgetScrollEndGutter` instead — a scrollport wants slack
  // at the end of its travel, and this inset, on a wrapper the rows spill
  // past, never provided any (Spencer: "I can't scroll up more and that
  // feels bad"). Every widget scrollport ends on the same slack — the fixed
  // `SCROLL_END_GUTTER` floor plus half the scrollport — whoever owns it: a
  // body that owns its scrollport (a `bleed` table, a `bleed-bottom` list
  // under its own header, a `flush` feed) drops the card's bottom inset and
  // carries the slack as its own scroll content (`DataTable gutterBottom`,
  // the kit's `WidgetBleedScroller`, a trailing `WidgetScrollEndGutter`), so
  // it reaches the card's edge: with the inset kept, its edge fade sat 16px
  // above the card's edge and dimmed the last row over a blank band.
  const bodyPaddings = {
    paddingX: bleed || flushBody ? undefined : cardInset,
    paddingBottom:
      flushBody || bleedEnd ? undefined : autoHeight ? ("base" as const) : cardInset,
    paddingTop: flushBody ? undefined : showHeader ? ("base" as const) : cardInset,
  };

  // The floating hover cluster a headerless tile pins to its corner — an
  // overlay-tier chip that reads above any surface.
  const tileCluster =
    (canManage || clusterAvailable) && (actionCluster || headerAccessories != null) ? (
      <Box
        position="absolute"
        top={6}
        right={6}
        {...reveal}
        className={suppressClass}
      >
        <Box direction="row" align="center" gap="2xs" padding="2xs"
          surface="popover"
          rounded="md"
          border
        >
          {headerAccessories}
          {domainActions}
          {actionCluster}
        </Box>
      </Box>
    ) : null;

  const inner =
    headerMode === "none" ? (
      containerChrome ? (
        // Container chrome: the tile is NOT a whole-surface drag handle (the
        // nested grid owns its own background gestures) and the hover chrome
        // is the labeled pill rendered as a sibling of the Card below — the
        // card's overflow clip would swallow a border-straddling pill.
        // The body is FLUSH: no scroll wrapper (containers never legitimately
        // scroll — height derives from children — and `resolveFitTarget` must
        // not find a scroll viewport inside one) and no padding, so the
        // nested grid sits directly on the card and nested cells land ON page
        // cells (the reserved 1px border is the only residual phase error).
        <Box flex={1} fill="width" direction="column" onClick={handleSelect}>
          {body}
        </Box>
      ) : (
        <Box position="relative" flex={1} fill="width"
          direction="column"
          onClick={handleSelect}
          cursor={dragCursor}
          {...guardedDragProps}
        >
          <WidgetBodyFrame
            mode={autoHeight ? "auto-height" : flushBody ? "flush" : "scroll"}
            contentRef={contentRef}
            paddings={{ paddingX: cardInset, paddingY: cardInset }}
          >
            {body}
          </WidgetBodyFrame>
          {tileCluster}
        </Box>
      )
    ) : autoHeight ? (
      // AutoHeight: the span is content rounded UP to whole rows, so the
      // cell-filling Card is up to a full row taller than the content. The
      // sizer FILLS that leftover instead of hugging, which hands the body a
      // choice the row grid otherwise makes for it: a body that claims the
      // space (a drill table that grows) ends flush with the card, and one
      // that ignores it renders exactly as it would have, top-anchored
      // with the leftover as a quiet band below. Anchored, never centered —
      // the leftover is a function of the content height, so centering
      // re-splits it on every growth and moves everything ABOVE the change: a
      // strip of numbers that opens a table beneath itself would shove its own
      // numbers by up to half a row. No scroll wrapper — content never
      // scrolls; the grid grows the row span instead.
      <>
        {header}
        <Box
          flex={1}
          minHeight={0}
          direction="column"
          onClick={handleSelect}
        >
          <Box ref={contentRef} grow shrink={false} direction="column" {...bodyPaddings}>
            {body}
          </Box>
        </Box>
      </>
    ) : (
      <>
        {header}
        <Box position="relative" minHeight={0} flex={1} direction="column" onClick={handleSelect}>
          <WidgetBodyFrame
            mode={flushBody ? "flush" : "scroll"}
            contentRef={contentRef}
            paddings={bodyPaddings}
          >
            {body}
          </WidgetBodyFrame>
        </Box>
      </>
    );

  // Container pill: labeled hover chrome straddling the container's top-left
  // border. A sibling of the Card inside the hover-group wrapper below —
  // inside the Card the overflow clip would swallow the protruding half, and
  // the `group-hover` reveal needs the group class on an ANCESTOR, which a
  // bare sibling never satisfies. Always visible on touch devices (CSS hover
  // never fires there). `GRID_LAYER.pill` keeps the protruding half above
  // later-in-DOM sibling tiles (grid items are zIndex:auto at rest, so the
  // pill's z participates at grid level).
  const pillLabel = def ? (
    <Box direction="row" align="center" gap="2xs" paddingY="2xs">
      <Icon name={def.icon} size="sm" color="muted" />
      <Text size="xs" weight="medium" variant="muted">
        {def.title}
      </Text>
    </Box>
  ) : null;
  const containerPill =
    containerChrome && (canManage || arrange) && def ? (
      <Box
        position="absolute"
        top={-12}
        left={12}
        zIndex={GRID_LAYER.pill}
        {...reveal}
        className={suppressClass}
      >
        <Box direction="row" align="center" gap="2xs"
          surface="popover"
          rounded="md"
          border
          paddingX="xs"
        >
          {guardedDragProps ? (
            <Box
              cursor={isDragging ? "grabbing" : "grab"}
              {...guardedDragProps}
            >
              {pillLabel}
            </Box>
          ) : (
            pillLabel
          )}
          {headerAccessories}
          {domainActions}
          {actionCluster}
        </Box>
      </Box>
    ) : null;

  const cardShapeClass = cn(
    // Height: every card fills its cell — autoHeight included. An autoHeight
    // span is content px rounded UP to whole rows, so the cell is up to a full
    // row taller than the content; filling parks that remainder INSIDE the
    // card, where `inner` centers it as symmetric breathing room, keeping the
    // board's inter-card gap exactly the grid gap. (Hugging was tried: the
    // remainder landed BETWEEN stacked cards, where it reads as a hole in the
    // board, not page rhythm.)
    "w-full h-full p-0 overflow-hidden",
    // Container chrome: the card's top+bottom 1px borders live INSIDE the
    // grid cell, but the flush nested grid runs fixed-pitch rows that need
    // the cell's FULL height — without compensation the last nested row
    // overflows the card's clip line by exactly 2px, swallowing children's
    // bottom borders and most of their s-resize handles. The negative margin
    // lets the flex-filled card grow those 2px back (into the ≥8px page gap
    // below — invisible), landing the last row's bottom edge exactly on the
    // clip line.
    containerChrome && "-mb-0.5",
    cardDragProps && (isDragging ? "cursor-grabbing" : "cursor-grab"),
  );

  // The hairline cue, merged LAST below. Everything before it writes a border
  // color of its own — the surface's on a card that names one, transparent on
  // one pinned to the canvas — and tw-merge keeps whichever class comes later,
  // so a cue merged earlier is dropped and selection paints nothing.
  //
  // Manageable cards hint their bounds on hover; `selected` outranks all hover
  // cues.
  const borderCueClass = selected
    ? "border-brand"
    : arrange
      ? painted
        ? HOVER_BORDER[paints]
        : "hover:border-border transition-colors"
      : undefined;
  // Container cards move the hover group to the wrapper below (the pill is a
  // Card sibling and `group-hover` needs an ancestor group) and skip the
  // widgetcard stamp: the suppression rule keys on descendant
  // `[data-widgetcard]:hover`, and with the group on the wrapper the
  // container's OWN card would match — hovering the container itself would
  // hide its pill. Child cards (never containers; depth is 1) keep the stamp
  // so the suppression matches exactly them.
  const cardStamp = containerChrome ? undefined : { [WIDGET_CARD_ATTR]: "" };

  const card = (
    <Card
      ref={cardSizeRef}
      // The anchor a body's own overlay pins to (`widgetCardDomId`): the
      // whole card, header included.
      id={widgetCardDomId(instance.id)}
      radius="lg"
      density={cardDensity}
      selected={selected}
      // Container cards move the hover group to the wrapper below.
      group={containerChrome ? undefined : hoverScope}
      // ONE prop decides the whole appearance: the card names a surface and
      // Box paints that surface's fill, hairline and drop. `elevated` is a
      // card floating over the page — longer drop, quieter hairline. A canvas
      // card names the BOARD's surface instead, so its fill matches the
      // substrate exactly and it masks the authoring guide dots by
      // construction. Dragging borrows the `popover` shadow, the one moment
      // the card really is above the page, so only the shadow changes and
      // never the color.
      surface={painted ? paints : canvas}
      surfaceShadow={painted && isDragging ? "popover" : undefined}
      className={cn(
        cardShapeClass,
        painted
          ? cn(
              "transition-shadow",
            )
          : // Shape only. `chromeless` goes one further and drops the fill
            // too: the widget renders as content, not as a card.
            cn(CANVAS_CHROME, chromeless && "bg-transparent"),
        borderCueClass,
      )}
      // A screen reader names each card by its title.
      role="group"
      aria-label={resolvedTitle || undefined}
      {...cardStamp}
      {...cardDragProps}
    >
      {accentBar}
      {inner}
    </Card>
  );

  const dressed = !containerPill ? (
    card
  ) : (
    // The pill's hover-group ancestor: fills the grid item, so the pill
    // anchors to the item's top/left.
    <Box fill="size" position="relative" group={hoverScope}>
      {card}
      {containerPill}
    </Box>
  );

  return (
    <WidgetViewStateProvider>
      <WidgetSurfaceProvider value={bodySurface}>{dressed}</WidgetSurfaceProvider>
    </WidgetViewStateProvider>
  );
}
